import { useEffect, useState } from "react";
import { PhotoThumb } from "@/components/scan/Scan";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FormError } from "@/components/common/ui-bits";
import { BillFormDialog, BillStatus, period } from "@/components/bills/BillParts";
import { api, errMsg } from "@/lib/api";
import { AMOUNT_ERROR, DATE_ERROR, PAYMENT_METHODS, centsToInput, formatDate, isValidAmount, money, paymentLabel, todayNassau } from "@/lib/format";

function PaymentForm({ bill, editing, onDone }) {
  const empty = { date: todayNassau(), amount: "", payment_method: "cash", note: "" };
  const [f, setF] = useState(empty);
  const [error, setError] = useState("");
  useEffect(() => { setF(editing ? { ...editing, amount: centsToInput(editing.amount_cents) } : empty); setError(""); }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    if (!isValidAmount(f.amount)) return setError(AMOUNT_ERROR);
    if (!f.date || f.date > todayNassau()) return setError(DATE_ERROR);
    try {
      const body = { ...f, amount: String(f.amount).replace(/[,$]/g, "") };
      const { data } = editing ? await api.put(`/bills/${bill.id}/payments/${editing.id}`, body) : await api.post(`/bills/${bill.id}/payments`, body);
      toast.success(editing ? "Payment updated" : "Payment recorded");
      setF(empty);
      onDone(data);
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} noValidate className="rounded-2xl border border-border p-3 space-y-2" data-testid="payment-form">
      <div className="text-sm font-semibold">{editing ? "Edit payment" : "Record a payment"}</div>
      <div className="grid grid-cols-2 gap-2">
        <Input inputMode="decimal" placeholder="Amount 0.00" value={f.amount} onChange={(e) => { setF({ ...f, amount: e.target.value }); setError(""); }} className="h-10 rounded-xl font-num" data-testid="payment-amount-input" />
        <Input type="date" max={todayNassau()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-10 rounded-xl" data-testid="payment-date-input" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PAYMENT_METHODS.map((p) => (
          <button type="button" key={p.value} onClick={() => setF({ ...f, payment_method: p.value })} data-testid={`payment-method-${p.value}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${f.payment_method === p.value ? "bg-primary border-primary" : "bg-card border-border"}`}>{p.label}</button>
        ))}
      </div>
      <Input placeholder="Note (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-10 rounded-xl" data-testid="payment-note-input" />
      <FormError message={error} testId="payment-form-error" />
      <div className="flex gap-2">
        <Button type="submit" className="h-10 rounded-xl flex-1" data-testid="payment-submit">{editing ? "Save payment" : "Record payment"}</Button>
        {editing && <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => onDone(null)}>Cancel</Button>}
      </div>
    </form>
  );
}

export function BillDetailDialog({ billId, onClose, onChanged }) {
  const [bill, setBill] = useState(null);
  const [editingPay, setEditingPay] = useState(null);
  const [editBill, setEditBill] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setBill(null); if (billId) api.get(`/bills/${billId}`).then((r) => setBill(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [billId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!billId) return null;
  const update = (data) => { if (data) setBill(data); setEditingPay(null); onChanged(); };
  const delPay = async (p) => { try { update((await api.delete(`/bills/${bill.id}/payments/${p.id}`)).data); toast.success("Payment deleted"); } catch (e) { toast.error(errMsg(e)); } };
  const delBill = async () => { await api.delete(`/bills/${bill.id}`); toast.success("Bill deleted"); onChanged(); onClose(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-card" data-testid="bill-detail-dialog">
        {!bill ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : (<>
          <DialogHeader><DialogTitle className="font-heading text-xl pr-6">{bill.vendor}{bill.bill_number && ` · ${bill.bill_number}`}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <BillStatus status={bill.status} testId="bill-detail-status" /><span>{bill.category_name}</span>{bill.account_number && <span>· Acct {bill.account_number}</span>}
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {[["Owed", bill.amount_cents, "owed"], ["Paid", bill.paid_cents, "paid"], ["Balance", bill.balance_cents, "balance"]].map(([l, v, k]) => (
              <div key={k} className="rounded-xl bg-secondary/60 px-3 py-2"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-num font-semibold" data-testid={`bill-detail-${k}`}>{money(v)}</div></div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground">Period {period(bill)} · Due {formatDate(bill.due_date)}{bill.notes && ` · ${bill.notes}`}</div>
          {bill.photo_id && <div className="flex items-center gap-2 text-xs text-muted-foreground"><PhotoThumb photoId={bill.photo_id} testId="bill-detail-photo" />Bill photo · tap to open</div>}
          <div>
            <div className="text-sm font-semibold mb-1">Payments</div>
            {bill.payments.length === 0 ? <div className="text-xs text-muted-foreground">No payments yet</div> : (
              <ul className="divide-y divide-border rounded-xl border border-border" data-testid="bill-payments-list">
                {bill.payments.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`bill-payment-${p.id}`}>
                    <div className="flex-1 min-w-0"><div className="font-num font-semibold">{money(p.amount_cents)}</div><div className="text-[11px] text-muted-foreground truncate">{formatDate(p.date)} · {paymentLabel(p.payment_method)}{p.note && ` · ${p.note}`}</div></div>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingPay(p)} data-testid={`bill-payment-edit-${p.id}`} aria-label="Edit payment"><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => delPay(p)} data-testid={`bill-payment-delete-${p.id}`} aria-label="Delete payment"><Trash2 className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(bill.balance_cents > 0 || editingPay) && <PaymentForm bill={bill} editing={editingPay} onDone={update} />}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setEditBill(true)} data-testid="bill-edit-button"><Pencil className="h-4 w-4 mr-1" />Edit bill</Button>
            <Button variant="outline" className="flex-1 rounded-xl text-rose-600 border-rose-200" onClick={() => setConfirm(true)} data-testid="bill-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete bill</Button>
          </div>
          <BillFormDialog open={editBill} bill={bill} onClose={() => setEditBill(false)} onSaved={(d) => { setEditBill(false); update(d); toast.success("Bill saved"); }} />
          <AlertDialog open={confirm} onOpenChange={setConfirm}>
            <AlertDialogContent className="bg-card rounded-2xl" data-testid="bill-delete-confirm">
              <AlertDialogHeader><AlertDialogTitle>Delete this bill?</AlertDialogTitle>
                <AlertDialogDescription>{bill.payments.length ? `Its ${bill.payments.length} payment(s) and their expense transactions (${money(bill.paid_cents)}) will also be deleted.` : "This cannot be undone."}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={delBill} className="bg-rose-600 hover:bg-rose-700" data-testid="bill-delete-confirm-button">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
